/**
 * In-memory analysis cache + pending-save queue (no localStorage on kiosk devices).
 */

const cacheBySession = new Map();
const pendingBySession = new Map();

const getCacheKey = (sessionId) => String(sessionId || '').trim();

export const cacheData = (sessionId, data) => {
  const sessionIdStr = getCacheKey(sessionId);
  if (!sessionIdStr) return false;
  const existingData = getCachedData(sessionIdStr) || {};
  cacheBySession.set(sessionIdStr, {
    ...existingData,
    ...data,
    lastUpdated: Date.now(),
    sessionId: sessionIdStr,
  });
  return true;
};

export const getCachedData = (sessionId) => {
  const sid = getCacheKey(sessionId);
  if (!sid) return null;
  const data = cacheBySession.get(sid);
  if (!data) return null;
  if (data.sessionId && data.sessionId !== sid) return null;
  return data;
};

export const clearCache = (sessionId) => {
  const sid = getCacheKey(sessionId);
  if (!sid) return;
  cacheBySession.delete(sid);
};

export const queueSave = (sessionId, saveData, saveType) => {
  const sid = getCacheKey(sessionId);
  if (!sid) return;
  const pending = getPendingSavesForSession(sid);
  pending.push({
    sessionId: sid,
    saveType,
    data: saveData,
    timestamp: Date.now(),
    retries: 0,
  });
  pendingBySession.set(sid, pending);
};

export const getPendingSavesForSession = (sessionId) => {
  const sid = getCacheKey(sessionId);
  if (!sid) return [];
  return pendingBySession.get(sid) || [];
};

export const getAllPendingSaves = () => {
  const allPending = [];
  pendingBySession.forEach((pending) => {
    if (Array.isArray(pending)) allPending.push(...pending);
  });
  return allPending;
};

export const clearPendingSavesForSession = (sessionId) => {
  const sid = getCacheKey(sessionId);
  if (!sid) return;
  pendingBySession.delete(sid);
};

export const clearAllPendingSaves = () => {
  pendingBySession.clear();
};

export const removePendingSave = (sessionId, index) => {
  const sid = getCacheKey(sessionId);
  if (!sid) return;
  const pending = getPendingSavesForSession(sid);
  pending.splice(index, 1);
  pendingBySession.set(sid, pending);
};

export const cleanupOldCacheEntries = () => {
  /* no-op: in-memory only */
};
